import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import usePermissions from '../auth/usePermissions';
import useIsMobile from '../auth/useIsMobile';
import Logo from './Logo';
import { NAV, PINNED_NAV, filterNav } from './navItems';

export default function TopBar() {
  const {
    name,
    email,
    initials,
    isDirector,
    isRegionalManager,
    isLiaisonManager,
    isLiaison,
    logout,
  } = useAuth();
  // Top-bar title is fixed — every page already has its own <h1> under
  // the bar, so swapping the brand out for "Dashboard" / "Suppliers" /
  // etc. was repeating information and hiding the product identity.
  const title = 'Proxemics Portal';

  // Mobile nav drawer. The desktop sidebar is hidden under 960px, so on
  // phones the hamburger is the only way to reach the other pages.
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  // Close the drawer whenever the route changes (e.g. after tapping a
  // link) so it never lingers over the page the user just navigated to.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  // Lock background scroll while the drawer is open.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  // Capability flags (includes per-user overrides) decide which nav links show.
  const perms = usePermissions();
  const isMobile = useIsMobile();

  // Show the highest-privilege role the user holds, so a Director who's also
  // been tagged LiaisonManager for some plant still reads as "Director".
  const roleLabel =
    isDirector ? 'Director' :
    isRegionalManager ? 'Regional Manager' :
    isLiaisonManager ? 'Liaison Manager' :
    isLiaison ? 'Liaison' : null;
  const roleTone = isDirector ? 'primary' : isRegionalManager ? 'primary' : 'neutral';

  const renderDrawerLink = (n) => (
    <NavLink
      key={n.to}
      to={n.to}
      className={({ isActive }) => `mobile-nav-link${isActive ? ' active' : ''}`}
      onClick={() => setMenuOpen(false)}
    >
      {n.label}
    </NavLink>
  );

  return (
    <header className="topbar">
      {/* Hamburger — visible on mobile only (CSS), sits where the title
          would be. Opens the nav drawer with every sub-page. */}
      <button
        type="button"
        className="topbar-hamburger"
        aria-label="Open menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
      >
        <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
          <path
            d="M3 6h16M3 11h16M3 16h16"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <div className="title">{title}</div>
      {/* Compact centered brand logo shown on mobile only (CSS), in place
          of the desktop "Proxemics Portal" title + role badge. Full-color
          variant reads on the white top bar. */}
      <Link
        to="/dashboard"
        className="topbar-mobile-title"
        aria-label="Go to Dashboard"
      >
        <Logo variant="full-color" size={40} />
      </Link>
      <div className="spacer" />
      {roleLabel && (
        <span className={`badge ${roleTone} topbar-role`}>{roleLabel}</span>
      )}
      <div className="user">
        {/* Avatar + name block is a link to My Account — mirrors the
            standard web pattern where clicking your own avatar takes you
            to your profile. The Sign Out button stays outside the link
            so its click handler isn't swallowed by the navigation. */}
        <Link
          to="/my-account"
          className="user-link"
          title="Go to My Account"
        >
          <div className="avatar" title={email}>{initials || '?'}</div>
          <div className="user-meta">
            <div className="user-name" style={{ fontWeight: 600 }}>
              {name || 'Signed in'}
            </div>
            <div className="user-email muted tiny">{email}</div>
          </div>
        </Link>
        <button className="btn btn-outline btn-sm" onClick={logout}>
          Sign out
        </button>
      </div>

      {/* Mobile nav drawer + backdrop. Rendered only when open; the
          hamburger that opens it is itself hidden on desktop, so this
          can't appear on a wide screen. */}
      {menuOpen && (
        <>
          <div
            className="mobile-nav-backdrop"
            onClick={() => setMenuOpen(false)}
          />
          <nav className="mobile-nav" aria-label="Main menu">
            <div className="mobile-nav-header">
              <span>Menu</span>
              <button
                type="button"
                className="mobile-nav-close"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="mobile-nav-links">
              {filterNav(NAV, perms, isMobile).map(renderDrawerLink)}
            </div>
            <div className="mobile-nav-links mobile-nav-links-pinned">
              {filterNav(PINNED_NAV, perms).map(renderDrawerLink)}
            </div>
          </nav>
        </>
      )}
    </header>
  );
}
